/**
 * Phase 6b1 PR 1 — `useSettingsPersist` typed write wrapper.
 *
 * The load-bearing piece per safety gate #1 of the locked Phase 6b1
 * plan. Every Settings field writes through this adapter:
 *
 *   1. Allowlist of keys (no arbitrary writes to athlete_context).
 *   2. Type/shape per key (text · integer · enum · boolean · date).
 *   3. Validation rules (numeric range, required, etc.).
 *   4. Optimistic UI status state machine
 *      (idle → saving → saved → idle, or → error).
 *   5. Routes through `POST /api/context/:userId` (the existing
 *      canAccessUser-gated endpoint at api/index.js:6157-6168).
 *      NOT through `useAthleteContext.persist`'s direct Supabase
 *      upsert — that path stays in place for /v2/today, /v2/chat,
 *      /v2/onboarding, /v2/plan; migrating it now would change
 *      write semantics for those routes simultaneously (too much
 *      blast radius). Per safety gate #8, this PR routes the new
 *      write surface through the API endpoint while leaving the
 *      legacy hook unchanged.
 *
 * The exported `validateField` and `FIELD_SPECS` are pure helpers —
 * the test file covers them directly without React.
 *
 * The exported `useSettingsPersist` hook is the React-facing API.
 * It returns:
 *   - `status`   — per-key state machine for the field row chip
 *   - `errors`   — per-key error string for inline display
 *   - `saveField`— the typed write entry point
 *
 * Allowlist scope: PR 1 (this file) registers the 7 Profile fields
 * + the `_journey` enum used by the head-of-page JourneyChip. PRs
 * 2–4 extend the allowlist by appending to FIELD_SPECS; the hook
 * is generic over the union.
 */

import { useCallback, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useAthleteContext } from '../hooks/useAthleteContext';
import { PARQ_QUESTIONS } from '../fixtures/onboarding';
import {
  editGuardianHold,
  GUARDIAN_HOLD_MESSAGE,
  editAgeBelowMinimum,
  AGE_BELOW_MINIMUM_MESSAGE,
  editAgeBandRequired,
  AGE_BAND_REQUIRED_MESSAGE,
} from './planEdit';

// ───────────────────────────────────────────────────────────────────
// Allowlist
// ───────────────────────────────────────────────────────────────────

/**
 * Profile bucket — 7 vanilla-aligned keys.
 *
 * Sourced from `src/renderer/js/settings/settings-page.js:10-18`
 * (PROFILE_FIELDS). Keys are TitleCase because that's what vanilla
 * stores in `athlete_context.context_data`; cross-surface parity
 * (vanilla and React read/write the same row) requires identical
 * key shape until Phase 7 retires vanilla.
 */
export const PROFILE_KEYS = [
  'Name',
  // CW-6 — explicit "What should we call you?" address. snake_case (no vanilla
  // equivalent); the backend name chain reads `context_data.preferred_name` and
  // lets it win over the formal Name when addressing the athlete.
  'preferred_name',
  'Age',
  'Gender',
  'Grade',
  'School',
  'Sport',
  'Goal',
] as const;
export type ProfileKey = (typeof PROFILE_KEYS)[number];

/**
 * Journey enum values — matches vanilla `USER_JOURNEYS` constants
 * (`src/renderer/js/constants.js`). The JourneyChip head-of-page
 * picker writes this key. Internal `_`-prefixed key per vanilla
 * convention for system-managed flags.
 */
export const JOURNEY_VALUES = [
  'competitive_solo',
  'competitive_team',
  'time_goal',
  'health_fitness',
  'social',
] as const;
export type JourneyValue = (typeof JOURNEY_VALUES)[number];

/**
 * Phase 6b1.5 post-PR-3 follow-up — Profile field validation enums.
 *
 * Background: pre-this-change, `Gender`, `Grade`, `Sport` accepted
 * arbitrary free-text per vanilla parity (`kind: 'text'`). The
 * daughter's smoke test surfaced context pollution — saving
 * `Gender: 'Dog'` or `Sport: 'Soccer'` (a running app) was silently
 * accepted, then fed into the LLM coach as canonical context. These
 * three enums constrain the surface to a vetted option set.
 *
 * The label for the `Sport` field is renamed to "Discipline" in the
 * Profile section UI (per user direction 2026-05-10) — the
 * persisted KEY stays `'Sport'` for cross-surface parity with
 * vanilla `/sports.html#settings` until Phase 7 cutover. Label
 * change only; key shape preserved.
 *
 * Migration: no automatic remap of existing pollutant values (per
 * user — "after the cutover, we will reset all users"). Existing
 * stale values display as-is in the idle row; the next edit forces
 * the athlete to pick a valid option from the dropdown.
 */
export const GENDER_VALUES = [
  'Female',
  'Male',
  'Non-binary',
  'Prefer not to say',
] as const;
export type GenderValue = (typeof GENDER_VALUES)[number];

export const GRADE_VALUES = ['High School', 'College', 'N/A'] as const;
export type GradeValue = (typeof GRADE_VALUES)[number];

/** Running discipline — persisted under the `Sport` key for vanilla
 *  cross-surface compat; UI label is "Discipline". The option set
 *  covers the running-app surface area (no team sports). */
export const DISCIPLINE_VALUES = [
  'Cross-country',
  'Track',
  'Road',
  'Trail',
  'Triathlon',
  'Mixed',
] as const;
export type DisciplineValue = (typeof DISCIPLINE_VALUES)[number];

/**
 * Notification short keys — anchored to vanilla `NOTIFICATION_TYPES`
 * at `src/renderer/js/settings/settings-page.js:21-27`.
 *
 * SCHEMA: vanilla writes `context._notification_prefs[key] = bool`
 * (nested object). Per Phase 6b1 vanilla parity audit (locked
 * decision 2026-05-09 = option a), React preserves the same nested
 * shape — these are the SHORT keys that live INSIDE
 * `_notification_prefs`, not top-level keys. Default-true semantics:
 * absence of a key is treated as `true` (matches vanilla 1080).
 *
 * Replaces the legacy `NotificationKey` union from PR 1's draft
 * (which declared flat `notify_<key>` top-level keys per the
 * pre-audit PR-SEQUENCE doc — that shape would have desynced
 * vanilla and React forever post-cutover).
 */
export const NOTIFICATION_SHORT_KEYS = [
  'kudo_received',
  'plan_approved',
  'team_joined',
  'ai_escalation',
  'motivational_message',
  // NU-3 — plan-lifecycle reminders (the user-approved 6th notification
  // type, `plan_nudge`). Nested under `_notification_prefs` like the
  // rest; absence = enabled, so existing users are unaffected.
  'plan_nudge',
] as const;
export type NotificationShortKey = (typeof NOTIFICATION_SHORT_KEYS)[number];

/**
 * Training Profile bucket — text fields ported from vanilla
 * `TRAINING_PROFILE_FIELDS` (`src/renderer/js/settings/settings-page.js:254-257`)
 * plus VDOT as a directly-editable integer.
 *
 * Vanilla has a richer Training Profile (location, vitamin D risk,
 * pace chips with "Recalculate" button). Those flows involve
 * geolocation, server-side recomputation, and modal forms that are
 * out of Phase 6b1 PR 2 scope — they migrate in a later cross-
 * cutting phase.
 */
export type TrainingProfileKey = 'Weekly mileage' | 'Running experience' | '_vdot';

/**
 * Phase 6b1.5 — Paces cluster keys.
 *
 * 12 keys spanning Training Location, Vitamin D Risk derivation,
 * 5 training paces, race-result calibration basis, and the basis
 * timestamp. All write atomically via two `saveFields` call sites:
 *
 *   - Location modal save (4 keys) — `_city`, `_latitude`,
 *     `_longitude`, `_vitamin_d_risk`
 *   - Recalculate modal save (8 keys) — `_vdot`, 5 paces,
 *     `_pace_race_distance`, `_pace_race_time`, plus
 *     `_pace_basis_at` (the design's "X days ago" caption stamp)
 *     = 9 keys total when bundled
 *
 * Persisted shapes match vanilla `src/renderer/js/settings/
 * settings-page.js:498-516` (location) + `:609-616` (paces). Per
 * the engineer reconciliation note at
 * `.claude/references/phase-6b1.5-paces-design/ENGINEER-RECONCILIATION.md`:
 *
 *   - `_city` is a single concatenated string (e.g. "Boulder, CO"),
 *     NOT split into `_city` + `_state` + `_country`
 *   - Pace values are `MM:SS` strings (`"8:15"`), NOT numeric seconds
 *   - Distance is the canonical lowercase RaceDistanceKey (`'5k'`),
 *     NOT the display label (`'5K'`)
 *
 * Vanilla `_pace_basis_at` is NEW in Phase 6b1.5 — vanilla doesn't
 * persist a timestamp; the design introduces the "8 days ago"
 * caption which requires it. Safe additive write at every Recalc.
 */
export type PacesClusterKey =
  // Training Location modal write surface (4 keys)
  | '_city'
  | '_latitude'
  | '_longitude'
  | '_vitamin_d_risk'
  // Recalculate Paces modal write surface (8 keys + basis-at = 9)
  | '_easy_pace'
  | '_tempo_pace'
  | '_interval_pace'
  | '_race_pace'
  | '_long_run_pace'
  | '_pace_race_distance'
  | '_pace_race_time'
  | '_pace_basis_at';

/** Vitamin D risk band — derived client-side from `_latitude` at
 *  Location-modal Save time. `|lat| ≤ 35 → low · ≤ 45 → elevated ·
 *  > 45 → high` per vanilla settings-page.js:504. */
export const VITAMIN_D_RISK_VALUES = ['low', 'elevated', 'high'] as const;
export type VitaminDRiskValue = (typeof VITAMIN_D_RISK_VALUES)[number];

/** Canonical race-distance keys for `_pace_race_distance`. Mirrors
 *  `RaceDistanceKey` exported from `lib/vdot.ts`; restated here to
 *  avoid a settingsPersist→vdot import cycle (this module is
 *  read by every Settings consumer; vdot is read by both this
 *  module's tests and onboarding). Keep in sync. */
export const PACE_RACE_DISTANCE_KEYS = [
  '800m',
  '1mile',
  '3k',
  '5k',
  '10k',
  'half_marathon',
  'marathon',
  // Ruling 6 (2026-10-22): "3200m / 2 Mile" saves its pace basis; the server rule carries it too.
  '3200m',
] as const;
export type PaceRaceDistanceKey = (typeof PACE_RACE_DISTANCE_KEYS)[number];

/** Pace string regex — `MM:SS` with seconds restricted to 00–59.
 *  Used for `_easy_pace` / `_tempo_pace` / `_interval_pace` /
 *  `_race_pace` / `_long_run_pace`. */
const PACE_MMSS_PATTERN = /^\d{1,2}:[0-5]\d$/;

/** Race-result time regex — `MM:SS` OR `HH:MM:SS`. Used for
 *  `_pace_race_time`. Marathon times naturally exceed 60 min. */
const RACE_TIME_PATTERN = /^(\d{1,3}:)?\d{1,3}:[0-5]\d$/;

/**
 * Body Metrics bucket — canonical numeric values + display unit
 * preferences. Vanilla persists `_height_cm` / `_weight_kg` as
 * source-of-truth and `_height_unit` / `_weight_unit` as display
 * preferences (`src/renderer/js/settings/settings-page.js:632-689`).
 *
 * Per safety gate #2:
 *   - Unit handling: kg ↔ lb, cm ↔ in
 *   - Empty vs zero: `null` clears, `0` is a real numeric value
 *     (range-checked, not treated as empty)
 *   - Numeric range: 100–250 cm height, 30–200 kg weight
 *   - No prompt pollution — the API endpoint uses
 *     `extractColumnsFromContext` (storage.js:91-119) to also write
 *     `height_cm` / `weight_kg` columns server-side
 */
export type BodyMetricsKey =
  | '_height_cm'
  | '_weight_kg'
  | '_height_unit'
  | '_weight_unit'
  // PR 9 — `_body_metrics_date` (vanilla settings-page.js:637, 676,
  // 829) and `_weight_history` (vanilla 832–837) write atomically
  // with weight/height saves. The date stamp drives the "Last
  // updated YYYY-MM-DD" caption; the history feeds the RED-S
  // monitoring trail consumed by `api/index.js:2219`, the trainer
  // dashboard, and the auth-side append at `auth.js:840`.
  | '_body_metrics_date'
  | '_weight_history';

/** PR 9 — single entry in `_weight_history`. Vanilla writes
 *  `{date: 'YYYY-MM-DD', kg: number}` rows; the array is trimmed to the
 *  last 12 entries on every weight save (settings-page.js:832–837). */
export interface WeightHistoryEntry {
  date: string;
  kg: number;
}

export const HEIGHT_UNIT_VALUES = ['metric', 'imperial'] as const;
export type HeightUnitValue = (typeof HEIGHT_UNIT_VALUES)[number];

export const WEIGHT_UNIT_VALUES = ['kg', 'lbs'] as const;
export type WeightUnitValue = (typeof WEIGHT_UNIT_VALUES)[number];

/**
 * Health bucket — free-text fields ported from vanilla
 * `bindHealthSection` (`src/renderer/js/settings/settings-page.js:898-921`).
 *
 * Vanilla also surfaces a 7-question PAR-Q+ overlay at
 * `src/renderer/sports.html:1168-1227` that writes `_parq_completed` /
 * `_parq_flags` / `_parq_completed_at`. Per the locked Phase 6b1
 * deferrals structure, the React rewrite of that overlay is
 * deferred to a follow-up phase; PR 3 ships a status row + Retake
 * button that links to the existing vanilla overlay (cross-surface
 * parity preserved).
 */
// EC-2b — `_injury_status`/`_injury_site` are the structured clinical
// engine inputs (flat enums; the server resolver composes them).
export type HealthKey = '_allergies' | '_injuries' | '_injury_status' | '_injury_site';

/**
 * Preferences bucket — 3 toggles per locked Phase 6b1 PR 3 scope.
 *
 * Vanilla canonical keys verified at `src/renderer/js/settings/
 * settings-page.js:1023-1025, 1066`:
 *   - `distance_unit` (lowercase, no underscore prefix; what vanilla
 *     writes via `context[prefKey] = btn.dataset.val`).
 *     The TitleCase `'Distance unit'` legacy key in INTERNAL_KEYS is
 *     a separate read-only fallback — read with `?? 'mi'` chain
 *     through both, but write only the lowercase key.
 *   - `_temp_unit` — system flag, underscore-prefixed.
 *   - `_date_format` — system flag, underscore-prefixed.
 */
export type PreferencesKey = 'distance_unit' | '_temp_unit' | '_date_format';

export const DISTANCE_UNIT_VALUES = ['mi', 'km'] as const;
export type DistanceUnitValue = (typeof DISTANCE_UNIT_VALUES)[number];

export const TEMP_UNIT_VALUES = ['F', 'C'] as const;
export type TempUnitValue = (typeof TEMP_UNIT_VALUES)[number];

export const DATE_FORMAT_VALUES = ['MM/DD', 'DD/MM'] as const;
export type DateFormatValue = (typeof DATE_FORMAT_VALUES)[number];

/**
 * Training Modes bucket — single boolean key for the waiver gate.
 *
 * Vanilla writes `_mode_waiver_signed = true` after the athlete
 * confirms `MODE_WAIVER_TEXT` (`src/renderer/js/settings/settings-page.js:1259-1264`).
 * Vanilla checks strict equality (`=== true`) so a stored `null` /
 * `false` / missing key all read as "not signed". There is no
 * "un-sign" path in PR 4 — per `MODE_WAIVER_TEXT` clause #6 that's
 * a contact-support flow.
 *
 * The actual training mode (base / build / competition / taper /
 * recovery) lives on `training_plans.theme`, NOT in
 * `athlete_context`. Mode writes go through
 * `useSetPlanMode` → `POST /api/plans/:id/mode`, NOT through
 * `useSettingsPersist`. This is the locked-plan boundary.
 */
export type TrainingModesKey = '_mode_waiver_signed';

/**
 * Phase 7 PR 1 — Onboarding-completion flag keys.
 *
 * Written atomically by `usePersistOnboarding` at the end of the 7-step
 * onboarding flow (Step 7 CTA "Go to my dashboard"). These flags drive
 * the `TodayRealData` gate: when both are unset, the route redirects
 * the athlete to `/v2/onboarding`; when set, the dashboard renders.
 *
 * Pair-keys mirror the `_journey` / `_journey_set_at` pattern from PR 9 —
 * the boolean is the gate-readable flag, the `_at` companion is the
 * audit-trail timestamp. Stored as `YYYY-MM-DD` strings (kind: 'date')
 * to match the existing date-validation contract (line ~624).
 *
 * `_parq_completed` is set to `true` because Step 3 forces the athlete
 * to answer the PAR-Q questions before progressing — reaching the
 * Step 7 CTA implies PAR-Q completion. (Vanilla also writes
 * `_parq_completed_at`; we match.)
 */
export type OnboardingCompletionKey =
  | '_onboarding_completed'
  | '_onboarding_completed_at'
  | '_parq_completed'
  | '_parq_completed_at'
  | '_parq_flags';

/**
 * The full allowlist union, scoped to keys actually registered in
 * `FIELD_SPECS`. PR 1 registered ProfileKey + `_journey`; PR 2
 * extends with TrainingProfileKey + BodyMetricsKey; PR 3 extends
 * with HealthKey + PreferencesKey + the synthetic
 * `_notification_prefs` (whole-object) key; PR 4 adds
 * `_mode_waiver_signed`.
 */
export type SettingKey =
  | ProfileKey
  | '_journey'
  | '_journey_set_at'
  | TrainingProfileKey
  | BodyMetricsKey
  | HealthKey
  | PreferencesKey
  | '_notification_prefs'
  | TrainingModesKey
  | PacesClusterKey
  | OnboardingCompletionKey
  | '_guardian_share_results'
  // UAT-B2 — 13–15 guardian-invite beat audit-trail keys (ISO timestamps).
  | '_guardian_invite_skipped_at'
  | '_guardian_nudge_dismissed_at'
  | '_account_role'
  | '_birth_year'
  | '_birth_month';

export type SettingValue =
  | string
  | number
  | boolean
  | null
  | Record<string, boolean>
  | WeightHistoryEntry[]
  | string[];

// ───────────────────────────────────────────────────────────────────
// Per-key spec
// ───────────────────────────────────────────────────────────────────

export interface FieldSpec {
  /** TS-side type the spec accepts.
   *  `nested-boolean` (PR 3): the value is a `Record<string, boolean>`
   *  whose keys must all be members of `spec.enum`. Used for
   *  `_notification_prefs` to preserve vanilla's nested-object schema.
   *  `decimal` (PR 9): accepts a finite number (or numeric string) and
   *  rounds to the nearest multiple of `spec.precision` before range-
   *  checking. Used for `_weight_kg` so athletes can record 64.5 kg
   *  instead of being silently rounded to 64 or 65 on every save
   *  (vanilla `step="0.1"` parity, settings-page.js:720, 813). */
  kind:
    | 'text'
    | 'integer'
    | 'decimal'
    | 'enum'
    | 'boolean'
    | 'date'
    | 'iso'
    | 'nested-boolean'
    | 'weight-history'
    | 'string-array';
  /** Numeric constraints — for `kind: 'integer'` or `kind: 'decimal'`. */
  min?: number;
  max?: number;
  /** Decimal precision step (e.g., 0.1 for 0.1-kg precision). Defaults
   *  to 0.1 when omitted. Only consumed by `kind: 'decimal'`. */
  precision?: number;
  /** Allowed members — for `kind: 'enum'` (string-valued) or
   *  `kind: 'nested-boolean'` (the allowed sub-keys). */
  enum?: readonly string[];
  /** Max character length for text fields. Defaults to 200. */
  maxLength?: number;
  /** Phase 6b1.5 — optional regex pattern for `kind: 'text'`. When
   *  set, the trimmed input must match. Used by `_easy_pace` (`MM:SS`)
   *  and `_pace_race_time` (`MM:SS` or `HH:MM:SS`) so the persisted
   *  pace strings always parse correctly downstream. */
  pattern?: RegExp;
  /** Required: empty/null is rejected. */
  required?: boolean;
}

/**
 * Per-key specs. Adding a new key requires an explicit entry; the
 * allowlist check in `validateField` rejects keys not registered
 * here.
 */
export const FIELD_SPECS: Record<SettingKey, FieldSpec> = {
  // Profile bucket
  Name: { kind: 'text', maxLength: 100 },
  // CW-6 — "What should we call you?" Persists to context_data.preferred_name;
  // the backend name chain prefers it over the formal Name when addressing the
  // athlete (and never invents a name when nothing is set).
  preferred_name: { kind: 'text', maxLength: 80 },
  // B196 (spec §4.1, §5 row 20) — the floor reads 13, proven by a
  // source-level pin (`settingsPersist.ts:469`, per the spec's own anchor).
  // `parseAge`'s `[5,110]` sanity window is a DELIBERATE, unrelated
  // exception (non-goal 10) and does not move.
  Age: { kind: 'integer', min: 13, max: 99 },
  // Phase 6b1.5 post-PR-3 — enum-validated to stop context pollution
  // (the daughter's smoke surfaced "Dog" passing as a valid Gender
  // under the legacy `kind: 'text'` shape). See GENDER_VALUES /
  // GRADE_VALUES / DISCIPLINE_VALUES doc comments for the migration
  // posture and label-vs-key split.
  Gender: { kind: 'enum', enum: GENDER_VALUES },
  Grade: { kind: 'enum', enum: GRADE_VALUES },
  School: { kind: 'text', maxLength: 200 },
  Sport: { kind: 'enum', enum: DISCIPLINE_VALUES },
  Goal: { kind: 'text', maxLength: 500 },
  // Journey enum (head-of-page chip)
  _journey: { kind: 'enum', enum: JOURNEY_VALUES },
  // EC-2b — structured injury state (clinical engine input). Flat enum
  // keys so the standard persist path applies; the server resolver
  // (api/lib/planning/injuryState.js) composes them. 'none' = healthy
  // and overrides any stale free text in `_injuries`.
  _injury_status: { kind: 'enum', enum: ['none', 'active', 'recovering', 'history'] },
  _injury_site: { kind: 'enum', enum: ['knee', 'shin', 'foot_ankle', 'hip', 'back', 'other'] },
  // PR 9: First-time stamp written atomically alongside `_journey`
  // when the athlete first picks one (vanilla parity per
  // settings-page.js:1013). Once set, never overwritten — `JourneyChip`
  // gates the write on the existing-value check.
  _journey_set_at: { kind: 'date' },
  // Training Profile bucket
  'Weekly mileage': { kind: 'text', maxLength: 50 },
  // Phase 6b1.5 post-PR-3 — converted from free-text "5 years" string
  // to integer years so the persisted value is a clean numeric the
  // LLM can reason over without parsing. Range 0–50 covers couch-to-5K
  // newcomers through career masters runners.
  'Running experience': { kind: 'integer', min: 0, max: 50 },
  // Phase 6b1.5 PR 1 — `_vdot` upgrades from `integer` to `decimal`
  // with 0.1 precision. Vanilla `utils/vdot.js:69` writes 1-decimal
  // VDOT (e.g. 49.8); the legacy `integer` kind silently rounded
  // these to whole numbers on every save, losing precision. Same
  // pattern as the `_weight_kg` PR 9 fix #4.
  _vdot: { kind: 'decimal', min: 20, max: 90, precision: 0.1 },
  // Body Metrics bucket
  // 100–250 cm covers ~3'3" to ~8'2" — clinically wide range.
  _height_cm: { kind: 'integer', min: 100, max: 250 },
  // 30–200 kg covers ~66 to ~441 lbs — clinically wide range. PR 9
  // changes `kind` from 'integer' to 'decimal' with 0.1-kg precision
  // so a 64.5 kg athlete stops being rounded to 64 or 65 on every
  // save (vanilla parity per settings-page.js:720, 813).
  _weight_kg: { kind: 'decimal', min: 30, max: 200, precision: 0.1 },
  _height_unit: { kind: 'enum', enum: HEIGHT_UNIT_VALUES },
  _weight_unit: { kind: 'enum', enum: WEIGHT_UNIT_VALUES },
  // PR 9 — write-on-save stamp + RED-S trail. No per-field UI chip;
  // these ride along with height/weight saves via `saveFields`.
  _body_metrics_date: { kind: 'date' },
  _weight_history: { kind: 'weight-history' },
  // Health bucket (PR 3)
  _allergies: { kind: 'text', maxLength: 200 },
  _injuries: { kind: 'text', maxLength: 200 },
  // Preferences bucket (PR 3)
  distance_unit: { kind: 'enum', enum: DISTANCE_UNIT_VALUES },
  _temp_unit: { kind: 'enum', enum: TEMP_UNIT_VALUES },
  _date_format: { kind: 'enum', enum: DATE_FORMAT_VALUES },
  // Notifications bucket (PR 3) — vanilla nested shape.
  // Whole-object writes; sub-keys validated against
  // NOTIFICATION_SHORT_KEYS. Use `saveNotifPref` to write a single
  // sub-key; it merges + delegates through saveField.
  _notification_prefs: {
    kind: 'nested-boolean',
    enum: NOTIFICATION_SHORT_KEYS,
  },
  // Training Modes bucket (PR 4) — waiver gate.
  // Strict equality `=== true` semantics: stored `null`/`false`/
  // missing all read as "not signed", same as vanilla.
  _mode_waiver_signed: { kind: 'boolean' },

  // ───────────────────────────────────────────────────────────
  // Phase 6b1.5 — Paces cluster (12 keys)
  //
  // Allowlist entries for the Training Location modal save (4)
  // and the Recalculate Paces modal save (8 + _pace_basis_at = 9).
  // Both consumers ship in PR 2 / PR 3 of Phase 6b1.5; the
  // allowlist must land in PR 1 so saveFields can validate the
  // shape before any UI code is merged.
  //
  // Vanilla key shapes preserved per engineer reconciliation note:
  //   - _city = single concatenated string
  //   - pace values = MM:SS strings (NOT numeric seconds)
  //   - _pace_race_distance = canonical lowercase key
  // ───────────────────────────────────────────────────────────

  // Location modal write surface
  _city: { kind: 'text', maxLength: 200 },
  // ⚖️ FOUNDER RULING 2026-08-16 §8.2 — CITY LEVEL, 0.01 (≈ 1.1 km). Was
  // 0.0001 (≈ 11 m), which made the stored home fix PRECISE LOCATION in
  // Apple's App Privacy taxonomy for no benefit: the only two consumers are
  // WeatherKit and the vitamin-D latitude band, and both are city-scale
  // questions. Rounding here moves the row Precise → Coarse.
  //
  // This spec is the BACKSTOP, not the control — `decimal` rounds to the
  // precision step (see validateField), so anything reaching saveFields is
  // coarsened whatever the caller did. The control is rounding at capture in
  // LocationModal, which is what keeps a precise fix out of component state
  // and off the screen in the first place.
  _latitude: { kind: 'decimal', min: -90, max: 90, precision: 0.01 },
  _longitude: { kind: 'decimal', min: -180, max: 180, precision: 0.01 },
  _vitamin_d_risk: { kind: 'enum', enum: VITAMIN_D_RISK_VALUES },

  // Recalculate modal write surface — 5 paces (MM:SS strings)
  _easy_pace: { kind: 'text', maxLength: 6, pattern: PACE_MMSS_PATTERN },
  _tempo_pace: { kind: 'text', maxLength: 6, pattern: PACE_MMSS_PATTERN },
  _interval_pace: { kind: 'text', maxLength: 6, pattern: PACE_MMSS_PATTERN },
  _race_pace: { kind: 'text', maxLength: 6, pattern: PACE_MMSS_PATTERN },
  _long_run_pace: { kind: 'text', maxLength: 6, pattern: PACE_MMSS_PATTERN },

  // Recalculate modal write surface — race-result basis (3 keys)
  _pace_race_distance: { kind: 'enum', enum: PACE_RACE_DISTANCE_KEYS },
  _pace_race_time: { kind: 'text', maxLength: 9, pattern: RACE_TIME_PATTERN },
  // NEW in Phase 6b1.5 — basis-at timestamp drives the design's
  // "Based on 5K in 19:42 · 8 days ago" caption. No vanilla
  // equivalent; safe additive write at every Recalculate save.
  _pace_basis_at: { kind: 'date' },

  // ───────────────────────────────────────────────────────────
  // Phase 7 PR 1 — Onboarding-completion flags (4 keys)
  //
  // Written atomically by `usePersistOnboarding` at the end of the
  // 7-step onboarding flow. Pair-key pattern mirrors `_journey` /
  // `_journey_set_at` from PR 9. The boolean is the gate-readable
  // flag (consumed by TodayRealData's onboarding gate); the `_at`
  // companion is the audit-trail timestamp.
  //
  // `_parq_completed` is set to `true` because Step 3 of the
  // onboarding flow forces a PAR-Q answer set before the user can
  // progress — reaching Step 7 implies PAR-Q completion.
  //
  // Stored as YYYY-MM-DD strings (`kind: 'date'`) for consistency
  // with `_journey_set_at` and the existing date validation
  // (validateField line ~624). Generate via
  // `new Date().toISOString().slice(0, 10)`.
  // ───────────────────────────────────────────────────────────
  _onboarding_completed: { kind: 'boolean' },
  _onboarding_completed_at: { kind: 'date' },
  _parq_completed: { kind: 'boolean' },
  _parq_completed_at: { kind: 'date' },
  // The flagged PAR-Q question keys — machine-built by the retake
  // panel / onboarding Step 3, never user-typed. Members must be
  // PARQ_QUESTIONS keys (the same set usePersistOnboarding writes).
  _parq_flags: { kind: 'string-array', enum: PARQ_QUESTIONS.map((q) => q.key) },

  // ───────────────────────────────────────────────────────────
  // CG-4 — guardian meets/results sharing (1 key)
  //
  // Founder-ratified 2026-07-03: meets/results is ATHLETE-CONTROLLED.
  // The transparency panel's toggle (GuardiansSection) writes this;
  // GET /api/guardian/home only lights the meets/results slot when it
  // is strictly `true`. Default OFF — absent/false/null all read as
  // "not shared".
  // ───────────────────────────────────────────────────────────
  _guardian_share_results: { kind: 'boolean' },

  // ───────────────────────────────────────────────────────────
  // UAT-B2 — 13–15 guardian-invite beat audit trail (2 keys)
  //
  // `_guardian_invite_skipped_at`: stamped when a 13–15 athlete skips the
  //   onboarding invite beat. The wizard never re-shows the beat; the Today
  //   card resurfaces it exactly once (a session newer than the skip).
  // `_guardian_nudge_dismissed_at`: stamped by the Today card's "Not now" —
  //   the resurface never returns. Both ISO timestamps; presence is the
  //   signal (dismissed-at present ⇒ never nag again).
  // ───────────────────────────────────────────────────────────
  _guardian_invite_skipped_at: { kind: 'iso' },
  _guardian_nudge_dismissed_at: { kind: 'iso' },

  // ───────────────────────────────────────────────────────────
  // CG-7 — account-role intent (1 key)
  //
  // Set once by the onboarding step-0 fork. `guardian` marks a
  // guardian-first account: useGuardianMode grants the reduced
  // guardian nav before any guardianship is active, and the Today
  // gate routes to /guardian instead of athlete onboarding. Absent
  // = athlete (the default; the fork only writes `guardian`).
  // ───────────────────────────────────────────────────────────
  _account_role: { kind: 'enum', enum: ['athlete', 'guardian', 'coach'] },

  // ───────────────────────────────────────────────────────────
  // B2 (COPPA, founder-ratified 2026-07-04) — birth month+year (2 keys)
  //
  // The durable age source captured as a REQUIRED part of onboarding
  // Step 3; the derived `Age` (existing key) feeds the clinical bands,
  // the U16 reflow gate, and the under-13 consent gate. Wide spec
  // bounds — the UI enforces the real range (this-year−99 … −5).
  // ───────────────────────────────────────────────────────────
  _birth_year: { kind: 'integer', min: 1900, max: 2100 },
  _birth_month: { kind: 'integer', min: 1, max: 12 },
};

// ───────────────────────────────────────────────────────────────────
// Pure validator
// ───────────────────────────────────────────────────────────────────

export type ValidationResult =
  | { ok: true; value: SettingValue }
  | { ok: false; error: string };

/**
 * Validate + coerce a raw input value against the registered spec
 * for `key`. Returns `{ ok: true, value }` with the canonical
 * coerced value (trimmed text, numeric integer, etc.) OR
 * `{ ok: false, error }` with a user-facing error message.
 *
 * Empty string / null / undefined are treated as "clear the value":
 * - If the field is `required`, returns `error: 'Required'`.
 * - Otherwise returns `{ ok: true, value: null }` (clear).
 *
 * `key` MUST be a member of the allowlist; unknown keys return
 * `error: 'Unknown setting'` (and `unsafeKey: true` indicates a
 * caller-side bug — the type system should have prevented this).
 */
export function validateField(
  key: string,
  raw: unknown,
): ValidationResult {
  const spec = (FIELD_SPECS as Record<string, FieldSpec | undefined>)[key];
  if (!spec) return { ok: false, error: 'Unknown setting' };

  // Empty / null / undefined → "clear" or required-error.
  if (raw === '' || raw === null || raw === undefined) {
    if (spec.required) return { ok: false, error: 'Required' };
    return { ok: true, value: null };
  }

  switch (spec.kind) {
    case 'text': {
      if (typeof raw !== 'string') return { ok: false, error: 'Must be text' };
      const trimmed = raw.trim();
      if (trimmed === '') {
        if (spec.required) return { ok: false, error: 'Required' };
        return { ok: true, value: null };
      }
      const cap = spec.maxLength ?? 200;
      const sliced = trimmed.slice(0, cap);
      // Phase 6b1.5 — pace strings + race-result times use a pattern
      // gate so persisted values always parse downstream. Empty
      // string is already handled above (clear). The pattern check
      // runs AFTER the maxLength slice so a pace like `8:15` (4
      // chars) passes; a malformed `8:abc` (4 chars) is rejected.
      if (spec.pattern && !spec.pattern.test(sliced)) {
        return { ok: false, error: 'Invalid format' };
      }
      return { ok: true, value: sliced };
    }
    case 'integer': {
      const num = typeof raw === 'number' ? raw : Number(String(raw).trim());
      if (!Number.isFinite(num) || !Number.isInteger(num)) {
        return { ok: false, error: 'Must be a whole number' };
      }
      if (spec.min !== undefined && num < spec.min) {
        return { ok: false, error: `Must be at least ${spec.min}` };
      }
      if (spec.max !== undefined && num > spec.max) {
        return { ok: false, error: `Must be at most ${spec.max}` };
      }
      return { ok: true, value: num };
    }
    case 'decimal': {
      const num = typeof raw === 'number' ? raw : Number(String(raw).trim());
      if (!Number.isFinite(num)) {
        return { ok: false, error: 'Must be a number' };
      }
      // Round to nearest precision step. Mirrors vanilla
      // `Math.round(kg * 10) / 10` (settings-page.js:813).
      const precision = spec.precision ?? 0.1;
      const scale = Math.round(1 / precision);
      const rounded = Math.round(num * scale) / scale;
      if (spec.min !== undefined && rounded < spec.min) {
        return { ok: false, error: `Must be at least ${spec.min}` };
      }
      if (spec.max !== undefined && rounded > spec.max) {
        return { ok: false, error: `Must be at most ${spec.max}` };
      }
      return { ok: true, value: rounded };
    }
    case 'enum': {
      if (typeof raw !== 'string') return { ok: false, error: 'Must be text' };
      // Trim stray whitespace before the membership check — HTML
      // form inputs occasionally deliver `"  value  "` and the user
      // shouldn't be punished for that.
      const trimmed = raw.trim();
      if (!spec.enum?.includes(trimmed)) return { ok: false, error: 'Invalid choice' };
      return { ok: true, value: trimmed };
    }
    case 'boolean': {
      // Accept boolean directly OR truthy strings 'true' / 'false'
      // (form inputs deliver strings).
      if (typeof raw === 'boolean') return { ok: true, value: raw };
      if (raw === 'true') return { ok: true, value: true };
      if (raw === 'false') return { ok: true, value: false };
      return { ok: false, error: 'Must be true or false' };
    }
    case 'date': {
      if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        return { ok: false, error: 'Must be YYYY-MM-DD' };
      }
      return { ok: true, value: raw };
    }
    case 'iso': {
      // UAT-B2 — full ISO-8601 timestamp (audit-trail keys like
      // `_guardian_invite_skipped_at`). Sub-day precision matters: the Today
      // resurface compares the skip stamp against the app-session start, so a
      // day-granular `date` would mis-fire within the skipping session.
      if (typeof raw !== 'string' || Number.isNaN(Date.parse(raw))) {
        return { ok: false, error: 'Must be an ISO timestamp' };
      }
      return { ok: true, value: raw };
    }
    case 'string-array': {
      // Machine-built string arrays (`_parq_flags`). Empty array is a
      // valid value (a clean screening clears prior flags).
      if (!Array.isArray(raw)) {
        return { ok: false, error: 'Must be an array' };
      }
      const allowed = spec.enum ? new Set(spec.enum) : null;
      const out: string[] = [];
      for (const item of raw) {
        if (typeof item !== 'string') {
          return { ok: false, error: 'Each entry must be text' };
        }
        if (allowed && !allowed.has(item)) {
          return { ok: false, error: `Unknown entry: ${item}` };
        }
        if (!out.includes(item)) out.push(item);
      }
      return { ok: true, value: out };
    }
    case 'weight-history': {
      // PR 9 — vanilla `{date: ISO, kg: number}` entries, trimmed to
      // last 12. Caller is expected to pre-trim; we trim defensively
      // and validate per-entry shape. Vanilla also accepts a JSON-
      // string blob (settings-page.js:834, auth.js:842) — we accept
      // that shape too so cross-surface reads stay compatible.
      let arr: unknown = raw;
      if (typeof raw === 'string') {
        try {
          arr = JSON.parse(raw);
        } catch {
          return { ok: false, error: 'Must be JSON-parseable history' };
        }
      }
      if (!Array.isArray(arr)) {
        return { ok: false, error: 'Must be an array of weight entries' };
      }
      const validated: WeightHistoryEntry[] = [];
      for (const entry of arr) {
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
          return { ok: false, error: 'Each entry must be an object' };
        }
        const e = entry as Record<string, unknown>;
        if (typeof e.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(e.date)) {
          return { ok: false, error: 'Each entry needs a YYYY-MM-DD date' };
        }
        if (typeof e.kg !== 'number' || !Number.isFinite(e.kg)) {
          return { ok: false, error: 'Each entry needs a numeric kg' };
        }
        validated.push({ date: e.date, kg: e.kg });
      }
      // Defensive trim — keep last 12 even if caller forgets.
      return { ok: true, value: validated.slice(-12) };
    }
    case 'nested-boolean': {
      // Whole-object write: must be a Record<string, boolean> whose
      // keys are all in spec.enum. Used for `_notification_prefs`
      // (vanilla nested shape, locked decision option a).
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        return { ok: false, error: 'Must be an object of booleans' };
      }
      const allowed = new Set(spec.enum ?? []);
      const next: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (!allowed.has(k)) {
          return { ok: false, error: `Unknown sub-key: ${k}` };
        }
        if (typeof v !== 'boolean') {
          return { ok: false, error: `Sub-key ${k} must be boolean` };
        }
        next[k] = v;
      }
      return { ok: true, value: next };
    }
  }
}

// ───────────────────────────────────────────────────────────────────
// React hook
// ───────────────────────────────────────────────────────────────────

export type FieldStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseSettingsPersistResult {
  /** Per-key state machine — drives the inline status chip. Keys
   *  not yet edited are absent from the map (treat as 'idle'). */
  status: Partial<Record<SettingKey, FieldStatus>>;
  /** Per-key error string from the last write attempt. Cleared on
   *  the next saving transition. */
  errors: Partial<Record<SettingKey, string | null>>;
  /** Save a single field. Validates first; rejects unknown keys
   *  and bad shapes without firing a network request. On 2xx,
   *  applies the value to local state via `mergeContext` and
   *  transitions status: saving → saved (1.5 s hold) → idle.
   *  On failure, transitions to 'error' and surfaces the error
   *  string in `errors[key]` (persistent until next save). */
  saveField: (key: SettingKey, raw: unknown) => Promise<void>;
  /** Save multiple fields atomically — all-or-nothing. Used by
   *  Body Metrics in PR 2 to persist (`_height_cm`, `_height_unit`)
   *  or (`_weight_kg`, `_weight_unit`) together so the unit toggle
   *  + value can never desynchronize.
   *
   *  Validation runs against ALL keys first; if any fail, NONE are
   *  saved and per-key errors are surfaced. On the network error
   *  path, all keys transition to 'error' simultaneously. */
  saveFields: (updates: Partial<Record<SettingKey, unknown>>) => Promise<void>;
  /** Resolved notification preferences derived from
   *  `context._notification_prefs`. Default-true semantics: a key
   *  absent from the stored object resolves to `true` (matches
   *  vanilla `prefs[t.key] !== false` at settings-page.js:1080). */
  notifPrefs: Record<NotificationShortKey, boolean>;
  /** Save a single notification toggle — merges the change into the
   *  full `_notification_prefs` object and delegates through
   *  `saveField` (so the per-row chip lights up on the synthetic
   *  `_notification_prefs` SettingKey). Preserves vanilla nested
   *  shape per locked decision option (a). */
  saveNotifPref: (key: NotificationShortKey, value: boolean) => Promise<void>;
  /** PR 9 — in-flight optimistic intent per notification short key.
   *  Populated synchronously on `saveNotifPref` so `<Switch>` can
   *  render the new value immediately; cleared once the save
   *  resolves (success → state matches; failure → visual rolls back
   *  to last persisted `notifPrefs[key]`). Keys not currently being
   *  saved are absent. Drives `<Switch pendingValue={…}>`. */
  pendingNotifPrefs: Partial<Record<NotificationShortKey, boolean>>;
}

function resolveNotifPrefs(
  raw: Record<string, unknown>,
): Record<NotificationShortKey, boolean> {
  const stored = (raw._notification_prefs ?? {}) as Record<string, unknown>;
  const out = {} as Record<NotificationShortKey, boolean>;
  for (const key of NOTIFICATION_SHORT_KEYS) {
    // Default-true: key absent OR not strictly `false` resolves to true.
    out[key] = stored[key] !== false;
  }
  return out;
}

/** How long the "✓ Saved" chip persists before fading to idle.
 *  Matches motion/save-status-transitions.md. */
const SAVED_HOLD_MS = 1500;

/**
 * B196 L5 (spec §4.8, F8) — a Settings write refused with the guardian hold,
 * the RULE 1 floor, or the RULE 2 wall gets the spec's ratified sentence
 * instead of the generic "Couldn't save · retry"; every other failure keeps
 * that line unchanged. `lib/planEdit`'s predicates are the ONE place these
 * codes are recognised — nothing here invents a fourth spelling.
 */
function codedRefusalMessage(err: unknown): string | null {
  const coded = err && typeof err === 'object' ? (err as { code?: unknown }) : null;
  if (editGuardianHold(coded)) return GUARDIAN_HOLD_MESSAGE;
  if (editAgeBelowMinimum(coded)) return AGE_BELOW_MINIMUM_MESSAGE;
  if (editAgeBandRequired(coded)) return AGE_BAND_REQUIRED_MESSAGE;
  return null;
}

export function useSettingsPersist(): UseSettingsPersistResult {
  const { user } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const { raw, mergeContext } = useAthleteContext();

  // Latest raw context — used inside saveField without making it
  // re-create on every keystroke.
  const rawRef = useRef(raw);
  rawRef.current = raw;

  const [status, setStatus] = useState<Partial<Record<SettingKey, FieldStatus>>>({});
  const [errors, setErrors] = useState<Partial<Record<SettingKey, string | null>>>({});

  // PR 9 — per-key in-flight notification intent (optimistic UX).
  // Populated synchronously on click; cleared in the saveNotifPref
  // `finally` block after the network resolves.
  const [pendingNotifPrefs, setPendingNotifPrefs] = useState<
    Partial<Record<NotificationShortKey, boolean>>
  >({});

  // Track active "saved → idle" timers so consecutive saves on the
  // same field don't leak stale resets onto a new state.
  const savedTimers = useRef<Partial<Record<SettingKey, number>>>({});

  const saveField = useCallback<UseSettingsPersistResult['saveField']>(
    async (key, rawValue) => {
      // Cancel any pending "saved → idle" timer for this key.
      const pending = savedTimers.current[key];
      if (pending !== undefined) {
        window.clearTimeout(pending);
        delete savedTimers.current[key];
      }

      // 1. Validate (also re-checks allowlist defensively).
      const validation = validateField(key, rawValue);
      if (!validation.ok) {
        setStatus((s) => ({ ...s, [key]: 'error' }));
        setErrors((e) => ({ ...e, [key]: validation.error }));
        return;
      }

      // 2. Auth gate.
      if (!user) {
        setStatus((s) => ({ ...s, [key]: 'error' }));
        setErrors((e) => ({ ...e, [key]: 'Not signed in' }));
        return;
      }

      // 3. Pessimistic save: show 'saving', wait for 2xx, then
      // apply + show 'saved'. (Optimistic-with-rollback adds
      // bookkeeping without UX win at form-edit pace.)
      setStatus((s) => ({ ...s, [key]: 'saving' }));
      setErrors((e) => ({ ...e, [key]: null }));

      try {
        // B237 — patch-only. This used to send `{ context: nextContext }`,
        // a wholesale blob built from `rawRef.current` (a possibly-stale
        // in-memory copy) — the same destructive-overwrite shape B148
        // retired from the provider. The server always merges a body with
        // no `context` key (B148 ruling), so a single field save sends
        // only the key it sets.
        const res = await fetchAuthed(
          `/api/context/${encodeURIComponent(user.id)}`,
          {
            method: 'POST',
            body: JSON.stringify({ patch: { [key]: validation.value } }),
          },
        );
        if (!res.ok) {
          let code: string | undefined;
          try {
            const body = await res.json();
            if (body && typeof body.code === 'string') code = body.code;
          } catch {
            /* ignore body parse errors */
          }
          const err: Error & { code?: string } = new Error(`Server returned ${res.status}`);
          if (code) err.code = code;
          throw err;
        }

        // Apply to local state via the existing hook's deep-merge
        // semantics. mergeContext fires a redundant Supabase upsert
        // (because useAthleteContext.persist is unchanged), but
        // the API endpoint we just hit already wrote the canonical
        // row — so the redundant write upserts identical data and
        // is a harmless no-op until the eventual cleanup PR
        // retires the legacy persist path.
        mergeContext({ [key]: validation.value });

        setStatus((s) => ({ ...s, [key]: 'saved' }));
        savedTimers.current[key] = window.setTimeout(() => {
          setStatus((s) => (s[key] === 'saved' ? { ...s, [key]: 'idle' } : s));
          delete savedTimers.current[key];
        }, SAVED_HOLD_MS);
      } catch (err) {
        // Persistent error — chip stays until next save, per
        // motion/save-status-transitions.md. B196 L5 — a coded refusal
        // (the guardian hold, the floor, the wall) gets its own ratified
        // sentence instead of the generic retry line.
        setStatus((s) => ({ ...s, [key]: 'error' }));
        setErrors((e) => ({
          ...e,
          [key]: codedRefusalMessage(err) ?? "Couldn't save · retry",
        }));
        // eslint-disable-next-line no-console
        console.warn('[settingsPersist] save failed for', key, err);
      }
    },
    [user, fetchAuthed, mergeContext],
  );

  const saveFields = useCallback<UseSettingsPersistResult['saveFields']>(
    async (updates) => {
      const keys = Object.keys(updates) as SettingKey[];
      if (keys.length === 0) return;

      // Cancel any pending "saved → idle" timers for these keys.
      for (const key of keys) {
        const pending = savedTimers.current[key];
        if (pending !== undefined) {
          window.clearTimeout(pending);
          delete savedTimers.current[key];
        }
      }

      // 1. Validate ALL keys first — atomic semantics. If any fail,
      //    none save; surface per-key errors and bail.
      const validations: Partial<Record<SettingKey, ValidationResult>> = {};
      let anyInvalid = false;
      for (const key of keys) {
        const result = validateField(key, updates[key]);
        validations[key] = result;
        if (!result.ok) anyInvalid = true;
      }
      if (anyInvalid) {
        setStatus((s) => {
          const next = { ...s };
          for (const key of keys) {
            const v = validations[key];
            if (v && !v.ok) next[key] = 'error';
          }
          return next;
        });
        setErrors((e) => {
          const next = { ...e };
          for (const key of keys) {
            const v = validations[key];
            if (v && !v.ok) next[key] = v.error;
          }
          return next;
        });
        return;
      }

      // 2. Auth gate.
      if (!user) {
        setStatus((s) => {
          const next = { ...s };
          for (const key of keys) next[key] = 'error';
          return next;
        });
        setErrors((e) => {
          const next = { ...e };
          for (const key of keys) next[key] = 'Not signed in';
          return next;
        });
        return;
      }

      // 3. All valid — transition all keys to 'saving'.
      setStatus((s) => {
        const next = { ...s };
        for (const key of keys) next[key] = 'saving';
        return next;
      });
      setErrors((e) => {
        const next = { ...e };
        for (const key of keys) next[key] = null;
        return next;
      });

      // 4. Build the merged context with all canonical values.
      const canonicalUpdates: Record<string, unknown> = {};
      for (const key of keys) {
        const v = validations[key];
        if (v?.ok) canonicalUpdates[key] = v.value;
      }

      // 5. Single API call. B237 — patch-only (see `saveField`'s comment);
      //    the server merges a body with no `context` key.
      try {
        const res = await fetchAuthed(
          `/api/context/${encodeURIComponent(user.id)}`,
          {
            method: 'POST',
            body: JSON.stringify({ patch: canonicalUpdates }),
          },
        );
        if (!res.ok) {
          let code: string | undefined;
          try {
            const body = await res.json();
            if (body && typeof body.code === 'string') code = body.code;
          } catch {
            /* ignore body parse errors */
          }
          const err: Error & { code?: string } = new Error(`Server returned ${res.status}`);
          if (code) err.code = code;
          throw err;
        }

        mergeContext(canonicalUpdates);

        setStatus((s) => {
          const next = { ...s };
          for (const key of keys) next[key] = 'saved';
          return next;
        });
        // Schedule per-key "saved → idle" timers (each independently
        // tracks; consecutive saves on one key get clean restarts).
        for (const key of keys) {
          savedTimers.current[key] = window.setTimeout(() => {
            setStatus((s) => (s[key] === 'saved' ? { ...s, [key]: 'idle' } : s));
            delete savedTimers.current[key];
          }, SAVED_HOLD_MS);
        }
      } catch (err) {
        // B196 L5 — a coded refusal gets its own ratified sentence instead
        // of the generic retry line, same as the single-field save above.
        const refusal = codedRefusalMessage(err);
        setStatus((s) => {
          const next = { ...s };
          for (const key of keys) next[key] = 'error';
          return next;
        });
        setErrors((e) => {
          const next = { ...e };
          for (const key of keys) next[key] = refusal ?? "Couldn't save · retry";
          return next;
        });
        // eslint-disable-next-line no-console
        console.warn('[settingsPersist] saveFields failed for', keys, err);
      }
    },
    [user, fetchAuthed, mergeContext],
  );

  // Derive the resolved notification map every render. Cheap — 5
  // keys; no useMemo needed. Always reads `raw` (not `rawRef`) so
  // the consumer re-renders when context updates from elsewhere
  // (e.g., onboarding completion, server push).
  const notifPrefs = resolveNotifPrefs(raw);

  const saveNotifPref = useCallback<UseSettingsPersistResult['saveNotifPref']>(
    async (key, value) => {
      // PR 9 — optimistic intent. Set the pending value synchronously
      // so `<Switch pendingValue>` flips immediately. saveField never
      // throws (catches internally + transitions status to 'error'),
      // so we always reach the finally block to clear the intent.
      // On success: notifPrefs (derived from raw) reflects the new
      // value, pending clears, visual is consistent.
      // On failure: raw didn't change, pending clears, visual rolls
      // back to the last persisted state.
      setPendingNotifPrefs((p) => ({ ...p, [key]: value }));
      const current = (rawRef.current._notification_prefs ?? {}) as Record<string, boolean>;
      const merged: Record<string, boolean> = { ...current, [key]: value };
      // Delegate through saveField — validation enforces sub-key
      // membership in NOTIFICATION_SHORT_KEYS, persistence path goes
      // through the same atomic POST + state machine as every other
      // setting. The status/error chip uses the synthetic
      // `_notification_prefs` SettingKey; consumers track the
      // last-touched short-key locally if they want per-row chip.
      try {
        await saveField('_notification_prefs', merged);
      } finally {
        setPendingNotifPrefs((p) => {
          if (!(key in p)) return p;
          const next = { ...p };
          delete next[key];
          return next;
        });
      }
    },
    [saveField],
  );

  return {
    status,
    errors,
    saveField,
    saveFields,
    notifPrefs,
    saveNotifPref,
    pendingNotifPrefs,
  };
}
